import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../App'
import { supabase } from '../lib/supabase'
import { localDayISO } from '../lib/localDay'
import { anniversaryDates } from '../lib/memoryAnniversary'

// Reads the signed-in collector's own memories dated on today's month and day in
// an earlier year (IDEA-MEMORY-ANNIVERSARY-001). Both tables are owner-only under
// RLS (verified live 2026-10-02); user_id is matched too as a belt-and-braces scope.
const NONE = Object.freeze([])
// Exact dates only, so a real collector has a handful of matches. The cap is a
// guard, ordered oldest first so it can only ever drop the newest anniversaries,
// matching the card's oldest-first order.
const ROW_CAP = 200

export function useMemoryAnniversaries(enabled = true) {
  const { user } = useAuth()
  const userId = user?.id
  const today = localDayISO()
  const [state, setState] = useState({ key: '', shares: [], anchors: [] })

  useEffect(() => {
    if (!enabled || !userId) return undefined
    let active = true
    const key = `${userId}:${today}`
    const dates = anniversaryDates(today)
    Promise.all([
      supabase
        .from('share_log')
        .select('id,fragrance_id,direction,person_name,shared_on,note')
        .eq('user_id', userId)
        .in('shared_on', dates)
        .order('shared_on', { ascending: true })
        .order('id', { ascending: true })
        .limit(ROW_CAP),
      supabase
        .from('memory_anchors')
        .select('id,fragrance_id,anchor_name,anchor_date,notes')
        .eq('user_id', userId)
        .in('anchor_date', dates)
        .order('anchor_date', { ascending: true })
        .order('id', { ascending: true })
        .limit(ROW_CAP),
    ])
      .then(([shares, anchors]) => {
        if (!active) return
        // A failed read just means no card today; Home must never break over it.
        setState({ key, shares: shares.error ? [] : shares.data || [], anchors: anchors.error ? [] : anchors.data || [] })
      })
      .catch(() => { if (active) setState({ key, shares: [], anchors: [] }) })
    return () => { active = false }
  }, [enabled, userId, today])

  const ready = Boolean(enabled && userId) && state.key === `${userId}:${today}`
  return useMemo(() => ({
    shares: ready ? state.shares : NONE,
    anchors: ready ? state.anchors : NONE,
    today,
  }), [ready, state, today])
}
